import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import {
  CartActionTypes,
  CartAction,
  ICartState,
  ICartExperience,
  CART_LS_KEY,
  ICartStorageData,
} from 'types/cart';
import {
  DELETE_DISCOUNT_CODE,
  RESTORE_PERSISTED_DISCOUNT_CODE,
  VALIDATE_DISCOUNT_CODE_SUCCESS,
} from 'types/discount-code';
import {
  DELETE_CREDIT_CODE,
  VALIDATE_CREDIT_CODE_SUCCESS,
  DELETE_ALL_CREDIT_CODES,
  RESTORE_PERSISTED_CREDIT_CODES,
} from 'types/credit-codes';
import initialState from 'store/initial-state';
import { getLSItem, setLSItem, clearLSItem } from 'utils/localStorage';
import filterExpiredCartItems from 'utils/cart/filterExpiredCartItems';

const persistCartExperiences = (
  brandId: string,
  cartId: string,
  cartItems: Record<string, ICartExperience>,
  paymentIntentRevision: number,
  cartIdUpdatedAt?: number,
) => {
  const LSData: ICartStorageData = getLSItem(CART_LS_KEY);
  const persisted = LSData?.[brandId];
  // A tab still on an older cart must not write it back over the newer one.
  if (
    persisted &&
    persisted.cartId !== cartId &&
    (persisted.cartIdUpdatedAt ?? 0) > (cartIdUpdatedAt ?? 0)
  ) {
    return;
  }
  setLSItem(CART_LS_KEY, {
    ...LSData,
    [brandId]: {
      cartId,
      cartIdUpdatedAt,
      cartItems,
      paymentIntentRevision,
    },
    updatedAt: new Date().toISOString(),
  });
};

const findBrandIdForCart = (cartId: string): string | undefined => {
  const LSData: ICartStorageData = getLSItem(CART_LS_KEY);
  if (!LSData) return undefined;

  return Object.keys(LSData).find(
    (key) =>
      key !== 'updatedAt' &&
      typeof LSData[key] === 'object' &&
      LSData[key]?.cartId === cartId,
  );
};

// Revision- and cartId-only updates must not write this tab's item list:
// another tab may have changed the items since this tab last synced, and a
// whole-snapshot write would overwrite them in every tab. Items come from
// localStorage; this tab's items are only used when the brand has no entry yet.
const persistCartFieldsForBrand = (
  brandId: string,
  fallbackCartItems: Record<string, ICartExperience>,
  fields: {
    cartId: string;
    cartIdUpdatedAt?: number;
    paymentIntentRevision: number;
  },
) => {
  const LSData: ICartStorageData = getLSItem(CART_LS_KEY);
  const persisted = LSData?.[brandId];
  persistCartExperiences(
    brandId,
    fields.cartId,
    persisted?.cartItems ?? fallbackCartItems,
    Math.max(
      persisted?.paymentIntentRevision ?? 0,
      fields.paymentIntentRevision,
    ),
    fields.cartIdUpdatedAt,
  );
};

const persistRevisionForCurrentCart = (
  state: ICartState,
  paymentIntentRevision: number,
) => {
  const brandId = findBrandIdForCart(state.cartId);
  if (brandId) {
    persistCartFieldsForBrand(brandId, state.experiences, {
      cartId: state.cartId,
      cartIdUpdatedAt: state.cartIdUpdatedAt,
      paymentIntentRevision,
    });
  }
};

/**
 * Whether a cart written by another tab is older than this tab's. A different
 * cartId is ordered by when each cart was created (revision only breaks ties,
 * e.g. entries saved before carts were stamped): revisions of two different
 * carts can collide when tabs write at the same moment. The same cart is
 * ordered by revision.
 */
const isIncomingCartStale = (
  state: ICartState,
  incoming: {
    cartId: string;
    cartIdUpdatedAt?: number;
    paymentIntentRevision: number;
  },
) => {
  const currentRevision = state.paymentIntentRevision ?? 0;
  if (incoming.cartId === state.cartId) {
    return incoming.paymentIntentRevision < currentRevision;
  }
  const incomingStamp = incoming.cartIdUpdatedAt ?? 0;
  const currentStamp = state.cartIdUpdatedAt ?? 0;
  if (incomingStamp !== currentStamp) return incomingStamp < currentStamp;
  return incoming.paymentIntentRevision < currentRevision;
};

const PI_REVISION_BUMP_ACTIONS: readonly string[] = [
  DELETE_DISCOUNT_CODE,
  VALIDATE_DISCOUNT_CODE_SUCCESS,
  DELETE_CREDIT_CODE,
  VALIDATE_CREDIT_CODE_SUCCESS,
  DELETE_ALL_CREDIT_CODES,
  RESTORE_PERSISTED_DISCOUNT_CODE,
  RESTORE_PERSISTED_CREDIT_CODES,
];

const RESTORED_PRICING_ACTIONS: readonly string[] = [
  RESTORE_PERSISTED_DISCOUNT_CODE,
  RESTORE_PERSISTED_CREDIT_CODES,
];

export default function cartReducer(
  state: ICartState = initialState.cart,
  action: CartAction,
): ICartState {
  if (PI_REVISION_BUMP_ACTIONS.includes((action as { type: string }).type)) {
    const newRevision = (state.paymentIntentRevision ?? 0) + 1;

    persistRevisionForCurrentCart(state, newRevision);

    return {
      ...state,
      isLastChangeRestored: RESTORED_PRICING_ACTIONS.includes(
        (action as { type: string }).type,
      ),
      paymentIntentRevision: newRevision,
    };
  }

  switch (action.type) {
    case CartActionTypes.SET_CART_ID: {
      // The new cart is stamped so every tab ranks it above the old one, even
      // when another tab persists a bump for the old cart at the same moment.
      const cartIdUpdatedAt = Date.now();
      const LSData: ICartStorageData = getLSItem(CART_LS_KEY);
      const newRevision =
        Math.max(
          LSData?.[action.payload.brandId]?.paymentIntentRevision ?? 0,
          state.paymentIntentRevision ?? 0,
        ) + 1;

      persistCartFieldsForBrand(action.payload.brandId, state.experiences, {
        cartId: action.payload.cartId,
        cartIdUpdatedAt,
        paymentIntentRevision: newRevision,
      });

      return {
        ...state,
        cartId: action.payload.cartId,
        cartIdUpdatedAt,
        loading: false,
        paymentIntentRevision: newRevision,
      };
    }

    case CartActionTypes.ADD_EXPERIENCE_TO_CART: {
      const newRevision = (state.paymentIntentRevision ?? 0) + 1;
      const newState = {
        ...state,
        experiences: {
          ...state.experiences,
          [action.payload.cartItemId]: action.payload.cartItemMetadata,
        },
        sessions: action.payload.sessions ?? null,
        loading: true,
        isLastChangeRestored: false,
        paymentIntentRevision: newRevision,
      };
      if (action.payload.persistMeta) {
        persistCartExperiences(
          action.payload.persistMeta.brandId,
          newState.cartId,
          newState.experiences,
          newRevision,
          newState.cartIdUpdatedAt,
        );
      }
      return newState;
    }
    case CartActionTypes.REMOVE_EXPERIENCE_FROM_CART: {
      const newRevision = (state.paymentIntentRevision ?? 0) + 1;
      const newState = {
        ...state,
        experiences: R.omit([action.payload.cartItemId], state.experiences),
        loading: false,
        isLastChangeRestored: false,
        paymentIntentRevision: newRevision,
      };

      if (action.payload.persistMeta) {
        persistCartExperiences(
          action.payload.persistMeta.brandId,
          newState.cartId,
          newState.experiences,
          newRevision,
          newState.cartIdUpdatedAt,
        );
      }

      return newState;
    }
    case CartActionTypes.LOAD_PERSISTED_CART_ITEMS: {
      const incomingRevision = action.payload.paymentIntentRevision ?? 0;

      // A write from another tab older than this tab's state is stale; adopting
      // it would roll this tab back to items/cartId it has already moved past.
      if (
        action.payload.fromStorageSync &&
        isIncomingCartStale(state, {
          cartId: action.payload.cartId,
          cartIdUpdatedAt: action.payload.cartIdUpdatedAt,
          paymentIntentRevision: incomingRevision,
        })
      ) {
        return state;
      }

      try {
        const validCartItems = filterExpiredCartItems(action.payload.cartItems);

        // If all items were expired, clear the cart and return initial state
        const originalItemCount = Object.keys(action.payload.cartItems).length;
        const validItemCount = Object.keys(validCartItems).length;

        if (originalItemCount > 0 && validItemCount === 0) {
          clearLSItem(CART_LS_KEY);
          return {
            ...initialState.cart,
            loading: false,
          };
        }

        return {
          ...state,
          cartId: action.payload.cartId,
          cartIdUpdatedAt:
            action.payload.cartIdUpdatedAt ?? state.cartIdUpdatedAt,
          experiences: validCartItems,
          isLastChangeRestored: true,
          paymentIntentRevision: incomingRevision,
          loading: false,
        };
      } catch (e) {
        clearLSItem(CART_LS_KEY);
        return {
          ...initialState.cart,
          loading: false,
        };
      }
    }

    case CartActionTypes.NO_NEED_TO_LOAD_PERSISTED_CART_ITEMS: {
      return {
        ...state,
        loading: false,
      };
    }

    case CartActionTypes.EDIT_CART_ITEM: {
      const newRevision = (state.paymentIntentRevision ?? 0) + 1;
      const newState = {
        ...state,
        experiences: {
          ...state.experiences,
          [action.payload.cartItemId]: {
            ...state.experiences[action.payload.cartItemId],
            ...action.payload.cartItemMetadata,
          },
        },
        loading: true,
        isLastChangeRestored: false,
        paymentIntentRevision: newRevision,
      };

      if (action.payload.persistMeta) {
        persistCartExperiences(
          action.payload.persistMeta.brandId,
          newState.cartId,
          newState.experiences,
          newRevision,
          newState.cartIdUpdatedAt,
        );
      }

      return newState;
    }
    case CartActionTypes.SET_CART_LOADING: {
      return {
        ...state,
        loading: action.payload.loading,
      };
    }
    case CartActionTypes.RESET_CART: {
      const newState = {
        ...initialState.cart,
        cartId: uuidv4(),
        cartIdUpdatedAt: Date.now(),
        loading: false,
        paymentIntentRevision: 0,
      };

      if (action.payload.persistMeta) {
        persistCartExperiences(
          action.payload.persistMeta.brandId,
          newState.cartId,
          newState.experiences,
          0,
          newState.cartIdUpdatedAt,
        );
      }

      return newState;
    }
    default:
      return state;
  }
}
